import {
  CameraIcon,
  CameraOffIcon,
  MicIcon,
  MicOffIcon,
  MonitorIcon,
  PauseIcon,
  PlayIcon,
  RecordIcon,
  RestartIcon,
  StopIcon,
  WaveformIcon,
} from '../components/icons';
import { Banner, Button, Kbd, Select, Toggle, cx } from '../components/ui';
import { bytes, clock } from '../lib/format';
import type { RecordingMode } from './codec';
import type { InputDevice, MediaPermissionState } from './media';
import { LONG_RECORDING_MS, type RecorderApi } from './useRecorder';

/**
 * Mode and source toggles before recording; transport controls during it.
 *
 * Pause, resume and restart are first-class here: same size, same row, always
 * visible, each with a shortcut printed next to it. They are not hidden behind
 * a menu, because the whole reason this product exists is that pausing a
 * recording should never cost you the take.
 *
 * The source toggles carry one more thing than an on/off: whether the browser
 * has been asked yet. Somebody whose microphone is remembered as on but not
 * yet granted must see it as *wanted and not running*, because the alternative
 * is a toggle that claims to be on while recording silence.
 */

const MODES: ReadonlyArray<{ value: RecordingMode; label: string; hint: string }> = [
  { value: 'screen', label: 'Screen', hint: 'Record a screen, window, or tab' },
  { value: 'audio', label: 'Audio only', hint: 'Record just your voice, no picture' },
];

export function ModePicker({
  mode,
  onChange,
  disabled,
  screenAvailable,
  audioAvailable,
}: {
  mode: RecordingMode;
  onChange: (mode: RecordingMode) => void;
  disabled: boolean;
  screenAvailable: boolean;
  audioAvailable: boolean;
}) {
  // Nothing to choose between: don't offer a choice.
  if (!screenAvailable || !audioAvailable) return null;

  return (
    <div className="flex flex-wrap items-center gap-3">
      <div
        role="radiogroup"
        aria-label="What to record"
        className="inline-flex rounded border border-hairline bg-slate-850 p-0.5"
      >
        {MODES.map((option) => (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={mode === option.value}
            disabled={disabled}
            onClick={() => onChange(option.value)}
            title={disabled ? 'Finish or discard this take to change what you are recording' : option.hint}
            className={cx(
              'flex items-center gap-2 rounded-xs px-3 py-1.5 text-body transition-colors duration-120',
              'disabled:opacity-40 disabled:pointer-events-none',
              mode === option.value ? 'bg-slate-700 text-chalk' : 'text-chalk-dim hover:text-chalk',
            )}
          >
            <span aria-hidden="true">
              {option.value === 'screen' ? <MonitorIcon size={14} /> : <WaveformIcon size={14} />}
            </span>
            {option.label}
          </button>
        ))}
      </div>
      <span className="text-caption text-chalk-faint">
        {mode === 'audio'
          ? 'A voice note: microphone only, no screen prompt.'
          : 'Your screen, with your voice and webcam if you want them.'}
      </span>
    </div>
  );
}

/**
 * What a toggle is actually showing.
 *
 *   live     the source is running and will be in the file.
 *   pending  wanted, remembered, and waiting on a permission we have not asked
 *            for yet. Pressing it is the gesture that asks.
 *   off      deliberately turned off. An input somebody has switched off is
 *            shown as off even where the browser would also refuse it: there
 *            is no point explaining a block around something nobody wants.
 *   blocked  wanted, and the browser is refusing. Pressing again would only be
 *            asking again, so the notice underneath carries the fix instead.
 */
type InputState = 'live' | 'pending' | 'off' | 'blocked';

function inputStateOf(
  live: boolean,
  wanted: boolean,
  permission: MediaPermissionState,
): InputState {
  if (live) return 'live';
  if (!wanted) return 'off';
  return permission === 'denied' ? 'blocked' : 'pending';
}

const STATE_HINT: Record<InputState, string> = {
  live: 'on',
  pending: 'not on yet',
  off: 'off',
  blocked: 'blocked',
};

/**
 * Which physical device to use. Only offered once the input is live, because
 * before a permission has been granted the browser reports every device with a
 * blank label, and a list of "Microphone 1, Microphone 2" is not a choice
 * anybody can make.
 */
function DevicePicker({
  label,
  devices,
  value,
  disabled,
  onChange,
}: {
  label: string;
  devices: InputDevice[];
  value: string | null;
  disabled: boolean;
  onChange: (deviceId: string | null) => void;
}) {
  if (devices.length < 2) return null;
  const present = value !== null && devices.some((device) => device.deviceId === value);

  return (
    <div className="flex flex-col gap-0.5">
      <Select
        aria-label={label}
        value={present && value ? value : ''}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value || null)}
        className="max-w-[13rem]"
      >
        <option value="">System default</option>
        {devices.map((device) => (
          <option key={device.deviceId} value={device.deviceId}>
            {device.label}
          </option>
        ))}
      </Select>
      {value !== null && !present ? (
        <span className="text-micro text-chalk-faint">
          The one you picked before is not connected, so this is the system default.
        </span>
      ) : null}
    </div>
  );
}

/**
 * The honest part. A refusal is explained once, without blame, with the place
 * to change it named, and it never blocks the record button in screen mode: a
 * walkthrough with no voice is still worth sending.
 */
function PermissionNotice({ recorder }: { recorder: RecorderApi }) {
  const audio = recorder.mode === 'audio';
  // In audio-only mode the microphone is not optional, so a block there is
  // always worth explaining. In screen mode it only matters for an input
  // somebody actually asked for.
  const micBlocked =
    recorder.micPermission === 'denied' && !recorder.hasMic && (audio || recorder.micWanted);
  const cameraBlocked =
    !audio && recorder.cameraPermission === 'denied' && !recorder.hasCamera && recorder.cameraWanted;

  if (!micBlocked && !cameraBlocked) return null;

  const both = micBlocked && cameraBlocked;
  const subject = both ? 'microphone and camera' : micBlocked ? 'microphone' : 'camera';
  const control = !micBlocked && cameraBlocked ? 'camera icon' : 'padlock icon';

  return (
    <Banner
      tone="caution"
      title={`Your browser is blocking the ${subject}`}
      action={
        <Button onClick={() => void recorder.requestInputs()}>I have allowed it, try again</Button>
      }
    >
      This is a browser setting rather than a Slate one, and it is not something you did wrong.
      Click the {control} at the left of the address bar, set{' '}
      {both ? 'Microphone and Camera' : micBlocked ? 'Microphone' : 'Camera'} to Allow, then press
      the button.{' '}
      {audio
        ? 'A voice note is nothing but the microphone, so this one is worth fixing before you record.'
        : 'You can also carry on without it: a screen recording with no voice is still useful.'}
    </Banner>
  );
}

export function SourceBar({ recorder }: { recorder: RecorderApi }) {
  const busy = recorder.phase === 'recording' || recorder.phase === 'paused' || recorder.phase === 'countdown';

  // Audio-only has exactly one source, and it is mandatory. Offering a mic
  // *toggle* would imply it could be turned off, which would leave nothing.
  if (recorder.mode === 'audio') {
    const blocked = recorder.micPermission === 'denied';
    return (
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <Toggle
            active={recorder.hasMic}
            pending={!recorder.hasMic && !blocked}
            onChange={() => void recorder.arm()}
            disabled={busy || recorder.phase === 'arming' || (blocked && !recorder.hasMic)}
            icon={recorder.hasMic ? <MicIcon size={15} /> : <MicOffIcon size={15} />}
            label={recorder.hasMic ? 'Change microphone' : 'Use my microphone'}
            hint={recorder.hasMic ? 'on' : blocked ? 'blocked' : 'not on yet'}
            title={
              busy
                ? 'The microphone is locked once recording starts; restart to change it'
                : recorder.hasMic
                  ? 'Pick a different microphone'
                  : blocked
                    ? 'Your browser is blocking the microphone; see the note below'
                    : 'Slate asks your browser for the microphone when you press this'
            }
          />
          {recorder.hasMic ? (
            <DevicePicker
              label="Which microphone to record"
              devices={recorder.micDevices}
              value={recorder.micDeviceId}
              disabled={busy}
              onChange={(deviceId) => void recorder.selectMicDevice(deviceId)}
            />
          ) : null}
          <span className="text-caption text-chalk-faint">
            {recorder.hasMic
              ? 'Ready when you are.'
              : blocked
                ? 'Nothing can be recorded until the microphone is allowed.'
                : 'Press this and your browser will ask whether Slate may listen. Nothing is recorded until you press Start recording.'}
          </span>
        </div>
        <PermissionNotice recorder={recorder} />
      </div>
    );
  }

  const micState = inputStateOf(recorder.hasMic, recorder.micWanted, recorder.micPermission);
  const cameraState = inputStateOf(
    recorder.hasCamera,
    recorder.cameraWanted,
    recorder.cameraPermission,
  );
  const waiting = micState === 'pending' || cameraState === 'pending';

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Toggle
          active={recorder.hasDisplay}
          onChange={() => void recorder.arm()}
          disabled={busy}
          icon={<MonitorIcon size={15} />}
          label={recorder.hasDisplay ? 'Change screen' : 'Choose screen'}
          title={
            recorder.hasDisplay
              ? 'Pick a different screen, window, or tab'
              : 'Pick a screen, window, or tab to record'
          }
        />
        <Toggle
          active={recorder.hasMic}
          pending={micState === 'pending'}
          onChange={() => void recorder.toggleMic()}
          disabled={busy || micState === 'blocked'}
          icon={recorder.hasMic ? <MicIcon size={15} /> : <MicOffIcon size={15} />}
          label="Microphone"
          hint={STATE_HINT[micState]}
          title={
            busy
              ? 'Sources are locked once recording starts; restart to change them'
              : micState === 'live'
                ? 'Stop recording your voice'
                : micState === 'pending'
                  ? 'Your browser will ask whether Slate may use your microphone'
                  : micState === 'blocked'
                    ? 'Your browser is blocking the microphone; see the note below'
                    : 'Record your voice'
          }
        />
        <Toggle
          active={recorder.hasCamera}
          pending={cameraState === 'pending'}
          onChange={() => void recorder.toggleCamera()}
          disabled={busy || cameraState === 'blocked' || !recorder.support.canvasCapture}
          icon={recorder.hasCamera ? <CameraIcon size={15} /> : <CameraOffIcon size={15} />}
          label="Webcam"
          hint={STATE_HINT[cameraState]}
          title={
            busy
              ? 'Sources are locked once recording starts; restart to change them'
              : cameraState === 'live'
                ? 'Stop burning a webcam bubble into the recording'
                : cameraState === 'pending'
                  ? 'Your browser will ask whether Slate may use your camera'
                  : cameraState === 'blocked'
                    ? 'Your browser is blocking the camera; see the note below'
                    : 'Burn a webcam bubble into the recording'
          }
        />
      </div>

      {recorder.hasMic || recorder.hasCamera ? (
        <div className="flex flex-wrap items-center gap-2">
          {recorder.hasMic ? (
            <DevicePicker
              label="Which microphone to record"
              devices={recorder.micDevices}
              value={recorder.micDeviceId}
              disabled={busy}
              onChange={(deviceId) => void recorder.selectMicDevice(deviceId)}
            />
          ) : null}
          {recorder.hasCamera ? (
            <DevicePicker
              label="Which camera to record"
              devices={recorder.cameraDevices}
              value={recorder.cameraDeviceId}
              disabled={busy}
              onChange={(deviceId) => void recorder.selectCameraDevice(deviceId)}
            />
          ) : null}
        </div>
      ) : null}

      {/* Says what will happen before it happens, which is the whole reason
          nothing was asked for when the page loaded. */}
      {waiting && !busy ? (
        <p className="text-caption text-chalk-faint">
          Your {micState === 'pending' && cameraState === 'pending'
            ? 'microphone and webcam are'
            : micState === 'pending'
              ? 'microphone is'
              : 'webcam is'}{' '}
          set to on but not running yet. Slate asks your browser for permission when you press the
          toggle, choose a screen, or start recording, and never before that.
        </p>
      ) : null}

      <PermissionNotice recorder={recorder} />
    </div>
  );
}

function UploadStatus({ recorder }: { recorder: RecorderApi }) {
  const { upload } = recorder;
  if (upload.phase === 'idle') return null;

  const percent =
    upload.capturedBytes > 0
      ? Math.min(100, Math.round((upload.uploadedBytes / upload.capturedBytes) * 100))
      : 0;

  return (
    <div className="flex items-center gap-2 text-caption text-chalk-faint">
      <span
        className={cx(
          'h-1.5 w-1.5 rounded-full',
          upload.error ? 'bg-danger' : upload.phase === 'done' ? 'bg-go' : 'bg-signal',
        )}
        aria-hidden="true"
      />
      <span className="tnum">
        {upload.error
          ? 'Upload stalled; it will be retried'
          : upload.phase === 'done'
            ? 'Uploaded'
            : `Uploading as you record · ${percent}% of ${bytes(upload.capturedBytes)}`}
      </span>
    </div>
  );
}

export function TransportBar({
  recorder,
  onCancel,
}: {
  recorder: RecorderApi;
  onCancel?: () => void;
}) {
  const { phase } = recorder;
  const live = phase === 'recording' || phase === 'paused';
  const tooLong = recorder.elapsedMs > LONG_RECORDING_MS;

  if (phase === 'idle' || phase === 'arming' || phase === 'ready' || phase === 'countdown') {
    return (
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button
            variant="record"
            size="lg"
            className="rounded-full pl-4"
            disabled={!recorder.armed || phase === 'arming' || phase === 'countdown'}
            busy={phase === 'arming'}
            onClick={recorder.start}
            title={
              recorder.armed
                ? undefined
                : recorder.mode === 'audio'
                  ? 'Allow the microphone first: a voice note cannot be silent'
                  : 'Pick a screen, window, or tab first'
            }
          >
            {phase !== 'arming' ? <RecordIcon size={12} /> : null}
            {phase === 'countdown' ? 'Starting…' : 'Start recording'}
          </Button>
          {recorder.armed && phase === 'ready' ? (
            <span className="hidden items-center gap-1.5 text-caption text-chalk-faint sm:flex">
              or press <Kbd>Space</Kbd>
            </span>
          ) : null}
        </div>
        {onCancel ? (
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        ) : null}
      </div>
    );
  }

  if (phase === 'finishing') {
    return (
      <div className="flex items-center gap-2 text-body text-chalk-dim">
        <Button variant="secondary" size="lg" busy disabled>
          Finishing up
        </Button>
        <span className="text-caption text-chalk-faint">
          Flushing the last few seconds to storage.
        </span>
      </div>
    );
  }

  if (!live) return null;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2 rounded-full border border-hairline bg-slate-850 px-3 py-1.5">
          <span
            className={cx(
              'h-2.5 w-2.5 rounded-full',
              phase === 'recording' ? 'animate-record-pulse bg-record' : 'bg-chalk-faint',
            )}
            aria-hidden="true"
          />
          <span className="slate-type text-body text-chalk" data-timecode>
            {clock(recorder.elapsedMs / 1000)}
          </span>
        </div>

        <Button
          variant="secondary"
          size="lg"
          onClick={recorder.togglePause}
          title={phase === 'recording' ? 'Pause (Space)' : 'Resume (Space)'}
        >
          {phase === 'recording' ? <PauseIcon size={15} /> : <PlayIcon size={15} />}
          {phase === 'recording' ? 'Pause' : 'Resume'}
        </Button>

        <Button
          variant="secondary"
          size="lg"
          onClick={recorder.restart}
          title={
            recorder.mode === 'audio'
              ? 'Discard this take and start again; your microphone stays open (Shift+R)'
              : 'Discard this take and start again; your screen stays shared (Shift+R)'
          }
        >
          <RestartIcon size={15} />
          Restart
        </Button>

        <Button variant="primary" size="lg" onClick={() => void recorder.stop('button')} title="Finish (Enter)">
          <StopIcon size={13} />
          Finish
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
        <UploadStatus recorder={recorder} />
        <span className="hidden items-center gap-1.5 text-caption text-chalk-faint sm:flex">
          <Kbd>Space</Kbd> pause
          <Kbd>Shift</Kbd>
          <Kbd>R</Kbd> restart
          <Kbd>Enter</Kbd> finish
        </span>
      </div>

      {tooLong ? (
        <p className="text-caption text-caution">
          This take is over 55 minutes. Long recordings hold a lot of{' '}
          {recorder.mode === 'audio' ? 'audio' : 'video'} in memory. Consider finishing and
          starting a fresh one.
        </p>
      ) : null}
    </div>
  );
}
